import React, { useState, useEffect, useRef } from 'react';
import { APP_VERSION, APP_BUILD } from '../version';
import { useAuth } from '../context/AuthContext';
import { api, useApi } from '../hooks/useApi';
import { useTheme } from '../context/ThemeContext';
import { useSearchParams } from 'react-router-dom';
import PhotoUpload from '../components/PhotoUpload';
import useMobile from '../hooks/useMobile';

const COLORS  = ['#7C6FF7','#F7605C','#34D399','#FBBF24','#60A5FA','#F472B6','#A78BFA','#FB923C'];
const AVATARS = ['👨','👩','👦','👧','👴','👵','🧑','👶','🐶','🐱','🦊','🐻'];

const ALL_IDS        = ['utseende','push','profil','familie','inviter','hjemsted','google','nyheter','admin-members'];
const ADMIN_ONLY_IDS = new Set(['inviter', 'hjemsted', 'nyheter', 'admin-members']);

const normalizeOrder = (saved) => {
  const valid   = (saved || []).filter(id => ALL_IDS.includes(id));
  const missing = ALL_IDS.filter(id => !valid.includes(id));
  return [...valid, ...missing];
};

// ─── Widget-kort ──────────────────────────────────────────────
function Widget({ id, icon, title, children, isDragging, isOver, onDragStart, onDragOver, onDrop, onDragEnd, onClose }) {
  const fromHandle = useRef(false);

  return (
    <div
      id={`settings-${id}`}
      draggable
      onDragStart={e => {
        if (!fromHandle.current) { e.preventDefault(); return; }
        onDragStart(e);
      }}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={() => { fromHandle.current = false; onDragEnd(); }}
      style={{
        ...s.widget,
        opacity:   isDragging ? 0.4 : 1,
        boxShadow: isOver && !isDragging ? 'inset 4px 0 0 #7C6FF7' : undefined,
        transition:'opacity .15s, box-shadow .1s',
      }}
    >
      <div style={s.widgetHeader}>
        <span
          style={s.dragHandle}
          title="Dra for å flytte"
          onMouseDown={() => { fromHandle.current = true; }}
          onMouseUp={()   => { fromHandle.current = false; }}
        >⠿</span>
        <span style={s.widgetIcon}>{icon}</span>
        <span style={s.widgetTitle}>{title}</span>
        <button style={s.closeBtn} onClick={onClose} title="Skjul widget">✕</button>
      </div>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={s.field}>
      <label style={s.lbl}>{label}</label>
      {children}
    </div>
  );
}

function Toast({ msg, type = 'success' }) {
  if (!msg) return null;
  return (
    <div style={{ ...s.toast, background: type === 'error' ? 'var(--red-bg)' : 'var(--green-bg)', color: type === 'error' ? 'var(--red)' : 'var(--green)' }}>
      {msg}
    </div>
  );
}

// ─── Hoved-komponent ─────────────────────────────────────────
export default function SettingsPage() {
  const isMobile = useMobile();
  const { user, family, members, refreshMembers, loginWithToken, logout } = useAuth();
  const { dark, toggle: toggleTheme } = useTheme();
  const [searchParams] = useSearchParams();
  const [toast,  setToast] = useState({ msg:'', type:'success' });
  const [wide,   setWide]  = useState(() => window.innerWidth > 700);

  // Scroll til seksjon når ?tab= er satt i URL
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (!tab) return;
    const attempt = (tries = 0) => {
      const el = document.getElementById(`settings-${tab}`);
      if (el) { el.scrollIntoView({ behavior:'smooth', block:'start' }); }
      else if (tries < 10) { setTimeout(() => attempt(tries + 1), 100); }
    };
    attempt();
  }, [searchParams]);

  const [widgetOrder, setWidgetOrder] = useState(() => {
    try { return normalizeOrder(JSON.parse(localStorage.getItem('settings-order'))); }
    catch { return [...ALL_IDS]; }
  });
  const [hidden, setHidden] = useState(() => {
    try { return JSON.parse(localStorage.getItem('settings-hidden')) || []; }
    catch { return []; }
  });
  const [dragging, setDragging] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [clearingCache, setClearingCache] = useState(false);

  useEffect(() => {
    const fn = () => setWide(window.innerWidth > 700);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  const notify = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg:'' }), 3500);
  };

  const clearCache = async () => {
    setClearingCache(true);
    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map(k => caches.delete(k)));
      }
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map(r => r.unregister()));
      }
      window.location.reload(true);
    } catch {
      setClearingCache(false);
      notify('Kunne ikke tømme cache', 'error');
    }
  };

  const { data: googleStatus, refresh: refreshGoogle }     = useApi('/api/google/status');
  const { data: famSettings,  refresh: refreshFamSettings } = useApi('/api/family/settings');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('google') === 'connected') {
      refreshGoogle();
      notify('Google koblet til!');
      window.history.replaceState({}, '', '/innstillinger');
    }
  }, [refreshGoogle]);

  // ── DnD handlers ──
  const handleDragStart = (id) => setDragging(id);
  const handleDragOver  = (id, e) => { e.preventDefault(); if (dragOver !== id) setDragOver(id); };
  const handleDrop      = (targetId) => {
    if (!dragging || dragging === targetId) { setDragging(null); setDragOver(null); return; }
    const order = [...widgetOrder];
    const from  = order.indexOf(dragging);
    const to    = order.indexOf(targetId);
    if (from === -1 || to === -1) { setDragging(null); setDragOver(null); return; }
    order.splice(from, 1);
    order.splice(to, 0, dragging);
    setWidgetOrder(order);
    localStorage.setItem('settings-order', JSON.stringify(order));
    setDragging(null);
    setDragOver(null);
  };
  const handleDragEnd = () => { setDragging(null); setDragOver(null); };

  // ── Lukk / gjenopprett ──
  const closeWidget = (id) => {
    const next = [...hidden, id];
    setHidden(next);
    localStorage.setItem('settings-hidden', JSON.stringify(next));
  };
  const restoreWidget = (id) => {
    const next = hidden.filter(h => h !== id);
    setHidden(next);
    localStorage.setItem('settings-hidden', JSON.stringify(next));
  };
  const resetLayout = () => {
    setWidgetOrder([...ALL_IDS]);
    setHidden([]);
    localStorage.removeItem('settings-order');
    localStorage.removeItem('settings-hidden');
  };

  // ── Filtrer ──
  const isVisible = (id) => {
    if (hidden.includes(id)) return false;
    if (ADMIN_ONLY_IDS.has(id) && user?.role !== 'admin') return false;
    if (id === 'google' && !googleStatus?.configured) return false;
    return true;
  };
  const isAccessible = (id) => {
    if (ADMIN_ONLY_IDS.has(id) && user?.role !== 'admin') return false;
    if (id === 'google' && !googleStatus?.configured) return false;
    return true;
  };

  const visibleOrder    = widgetOrder.filter(isVisible);
  const hiddenAccessible = hidden.filter(isAccessible);

  // ── Widget-etiketter (icon avhenger av dark) ──
  const LABELS = {
    utseende:       { icon: dark ? '☀️' : '🌙', title: 'Utseende' },
    push:           { icon: '🔔',  title: 'Push-varsler' },
    profil:         { icon: '👤',  title: 'Min profil' },
    familie:        { icon: '🏠',  title: 'Familien' },
    inviter:        { icon: '➕',  title: 'Inviter familiemedlem' },
    hjemsted:       { icon: '📍',  title: 'Hjemsted & vær' },
    google:         { icon: '🔗',  title: 'Google-integrasjon' },
    nyheter:        { icon: '📰',  title: 'Nyhetskilder (RSS)' },
    'admin-members':{ icon: '⚙️',  title: 'Administrer medlemmer' },
  };

  const dndFor = (id) => ({
    isDragging: dragging === id,
    isOver:     dragOver === id,
    onDragStart:() => handleDragStart(id),
    onDragOver: (e) => handleDragOver(id, e),
    onDrop:     () => handleDrop(id),
    onDragEnd:  handleDragEnd,
    onClose:    () => closeWidget(id),
  });

  const renderWidget = (id) => {
    const lbl    = LABELS[id];
    const shared = { key: id, id, ...lbl, ...dndFor(id) };

    switch (id) {

      case 'utseende': return (
        <Widget {...shared}>
          <div style={{ ...s.body, flexDirection:'row', alignItems:'center', justifyContent:'space-between' }}>
            <div>
              <div style={{ fontSize:15, fontWeight:700, color:'var(--txt)', marginBottom:2 }}>Mørkt tema</div>
              <div style={{ fontSize:12, color:'var(--txl)' }}>Bytt mellom lyst og mørkt grensesnitt</div>
            </div>
            <button type="button" onClick={toggleTheme} style={{
              position:'relative', width:52, height:28, borderRadius:14, border:'none', cursor:'pointer',
              background: dark ? '#7C6FF7' : 'var(--bdr)', transition:'background .2s', flexShrink:0,
            }}>
              <span style={{
                position:'absolute', top:3, left: dark ? 26 : 3,
                width:22, height:22, borderRadius:'50%', background:'#fff',
                boxShadow:'0 1px 4px rgba(0,0,0,.2)', transition:'left .2s',
                display:'flex', alignItems:'center', justifyContent:'center', fontSize:12,
              }}>{dark ? '🌙' : '☀️'}</span>
            </button>
          </div>
        </Widget>
      );

      case 'push': return (
        <Widget {...shared}><PushSection notify={notify} /></Widget>
      );

      case 'profil': return (
        <Widget {...shared}>
          <ProfileSection user={user} notify={notify} loginWithToken={loginWithToken} logout={logout} />
        </Widget>
      );

      case 'familie': return (
        <Widget {...shared}>
          <FamilySection family={family} members={members} user={user} notify={notify} refreshMembers={refreshMembers} />
        </Widget>
      );

      case 'inviter': return (
        <Widget {...shared}><InviteSection notify={notify} refreshMembers={refreshMembers} /></Widget>
      );

      case 'hjemsted': return (
        <Widget {...shared}>
          <LocationSection famSettings={famSettings} notify={notify} refresh={refreshFamSettings} />
        </Widget>
      );

      case 'google': return (
        <Widget {...shared}>
          <GoogleSection googleStatus={googleStatus} refreshGoogle={refreshGoogle} notify={notify} />
        </Widget>
      );

      case 'nyheter': return (
        <Widget {...shared}>
          <NewsSection famSettings={famSettings} notify={notify} refresh={refreshFamSettings} />
        </Widget>
      );

      case 'admin-members': return (
        <Widget {...shared}>
          <AdminMembersSection members={members} user={user} notify={notify} refreshMembers={refreshMembers} />
        </Widget>
      );

      default: return null;
    }
  };

  // ── Mobil-spesifikke tilstander (må være på toppnivå) ──
  const [showFull,      setShowFull]      = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [editForm,      setEditForm]      = useState({});
  const [editSaving,    setEditSaving]    = useState(false);
  const [showNotif,     setShowNotif]     = useState(false);

  if (isMobile) {

    const openMemberEdit = (m) => {
      setEditForm({ name: m.name||'', avatar: m.avatar||'', color: m.color||'#7C6FF7', birth_date: m.birth_date||'', role: m.role||'member' });
      setEditingMember(m);
    };
    const saveMemberEdit = async () => {
      setEditSaving(true);
      await api.put(`/api/auth/members/${editingMember.id}/profile`, {
        name: editForm.name, avatar: editForm.avatar, color: editForm.color, birth_date: editForm.birth_date||null,
      }).catch(()=>{});
      if (user?.role==='admin' && editingMember.id !== user.id) {
        await api.put(`/api/auth/members/${editingMember.id}/role`, { role: editForm.role }).catch(()=>{});
      }
      await refreshMembers();
      setEditSaving(false);
      setEditingMember(null);
      notify('Lagret!');
    };

    const AVATARS_LIST = ['👨','👩','👦','👧','👴','👵','🧑','👶','🐶','🐱','🦊','🐻','🦁','🐼','🐨','🐯'];
    const COLORS_LIST  = ['#7C6FF7','#E5534B','#34D399','#FBBF24','#60A5FA','#F472B6','#A78BFA','#FB923C','#2A5298','#1A7A4A'];

    const Row = ({label, value, onTap, rightEl, noBorder}) => (
      <div onClick={onTap} style={{ display:'flex', alignItems:'center', padding:'14px 16px', borderBottom: noBorder?'none':'1px solid '+(dark?'rgba(255,255,255,0.06)':'#F5F5F5'), cursor:onTap?'pointer':'default', gap:12 }}>
        <span style={{ flex:1, fontSize:15, color:txt, fontWeight:500 }}>{label}</span>
        {value && <span style={{ fontSize:14, color:sub }}>{value}</span>}
        {rightEl}
        {onTap && <span style={{ color:sub, fontSize:16 }}>›</span>}
      </div>
    );
    const Section = ({title, children}) => (
      <div style={{ marginBottom:20 }}>
        <div style={{ fontSize:12, fontWeight:700, color:sub, textTransform:'uppercase', letterSpacing:.5, padding:'0 16px', marginBottom:6 }}>{title}</div>
        <div className="glass-card" style={{ borderRadius:14, margin:'0 0', overflow:'hidden', padding:0 }}>{children}</div>
      </div>
    );
    const Toggle = ({on, onChange}) => (
      <button onClick={onChange} style={{ width:48, height:28, borderRadius:14, border:'none', cursor:'pointer', background:on?'#7C6FF7':'rgba(124,111,247,0.2)', position:'relative', flexShrink:0 }}>
        <span style={{ position:'absolute', top:3, left:on?22:3, width:22, height:22, borderRadius:'50%', background:'#fff', boxShadow:'0 1px 4px rgba(0,0,0,.2)', transition:'left .2s' }}/>
      </button>
    );

    const pageBg = dark ? '#0A0818' : '#EDE8FF';
    const txt    = dark ? '#E8E8F8' : '#1A1A35';
    const sub    = dark ? '#9090B8' : '#888';
    const border = dark ? 'rgba(255,255,255,.07)' : 'rgba(124,111,247,0.12)';

    return (
      <div style={{ background:pageBg, minHeight:'100%', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
        <Toast msg={toast.msg} type={toast.type}/>

        {/* ── Rediger familiemedlem — bunnark ── */}
        {editingMember && (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.6)', zIndex:600, display:'flex', alignItems:'flex-end' }}
            onClick={() => setEditingMember(null)}>
            <div style={{ background: dark?'#1A1245':'#fff', width:'100%', borderRadius:'20px 20px 0 0', padding:'20px 20px 40px', maxHeight:'85vh', overflowY:'auto' }}
              onClick={e => e.stopPropagation()}>
              <div style={{ width:36, height:4, background: dark?'rgba(255,255,255,0.2)':'#E0E0E0', borderRadius:4, margin:'0 auto 20px' }}/>
              <div style={{ fontSize:17, fontWeight:800, color:txt, marginBottom:20 }}>
                Rediger {editingMember.name}
              </div>

              {/* Navn */}
              <div style={{ marginBottom:16 }}>
                <div style={{ fontSize:12, fontWeight:700, color:sub, marginBottom:6 }}>NAVN</div>
                <input value={editForm.name} onChange={e=>setEditForm(f=>({...f,name:e.target.value}))}
                  style={{ width:'100%', padding:'12px 14px', borderRadius:12, border:'1px solid '+(dark?'rgba(255,255,255,0.12)':'rgba(124,111,247,0.2)'), background: dark?'rgba(255,255,255,0.08)':'rgba(255,255,255,0.7)', color:txt, fontSize:15, fontFamily:'inherit', outline:'none', boxSizing:'border-box' }}/>
              </div>

              {/* Avatar */}
              <div style={{ marginBottom:16 }}>
                <div style={{ fontSize:12, fontWeight:700, color:sub, marginBottom:8 }}>IKON</div>
                <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                  {AVATARS_LIST.map(a => (
                    <button key={a} onClick={()=>setEditForm(f=>({...f,avatar:a}))}
                      style={{ width:42, height:42, borderRadius:10, fontSize:22, border:`2px solid ${editForm.avatar===a?'#7C6FF7':'rgba(124,111,247,0.2)'}`, background: editForm.avatar===a?'rgba(124,111,247,0.15)':dark?'rgba(255,255,255,0.06)':'#F8F8F8', cursor:'pointer' }}>
                      {a}
                    </button>
                  ))}
                </div>
              </div>

              {/* Farge */}
              <div style={{ marginBottom:16 }}>
                <div style={{ fontSize:12, fontWeight:700, color:sub, marginBottom:8 }}>FARGE</div>
                <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                  {COLORS_LIST.map(c => (
                    <button key={c} onClick={()=>setEditForm(f=>({...f,color:c}))}
                      style={{ width:34, height:34, borderRadius:'50%', background:c, border:`3px solid ${editForm.color===c?txt:'transparent'}`, cursor:'pointer' }}/>
                  ))}
                </div>
              </div>

              {/* Fødselsdato */}
              <div style={{ marginBottom:16 }}>
                <div style={{ fontSize:12, fontWeight:700, color:sub, marginBottom:6 }}>FØDSELSDATO (valgfritt)</div>
                <input type="date" value={editForm.birth_date||''} onChange={e=>setEditForm(f=>({...f,birth_date:e.target.value}))}
                  style={{ width:'100%', padding:'12px 14px', borderRadius:12, border:'1px solid '+(dark?'rgba(255,255,255,0.12)':'rgba(124,111,247,0.2)'), background: dark?'rgba(255,255,255,0.08)':'rgba(255,255,255,0.7)', color:txt, fontSize:15, fontFamily:'inherit', outline:'none', boxSizing:'border-box' }}/>
              </div>

              {/* Rolle (kun admin, ikke seg selv) */}
              {user?.role==='admin' && editingMember.id !== user.id && (
                <div style={{ marginBottom:20 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:sub, marginBottom:8 }}>ROLLE</div>
                  <div style={{ display:'flex', gap:10 }}>
                    {[{v:'member',l:'👤 Medlem'},{v:'admin',l:'👑 Admin'}].map(r=>(
                      <button key={r.v} onClick={()=>setEditForm(f=>({...f,role:r.v}))}
                        style={{ flex:1, padding:'10px', borderRadius:12, border:`2px solid ${editForm.role===r.v?'#7C6FF7':border}`, background: editForm.role===r.v?'rgba(124,111,247,0.15)':dark?'rgba(255,255,255,0.06)':'#F8F8F8', fontWeight:700, fontSize:14, cursor:'pointer', fontFamily:'inherit', color: editForm.role===r.v?'#7C6FF7':txt }}>
                        {r.l}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <button onClick={saveMemberEdit} disabled={editSaving}
                style={{ width:'100%', padding:'14px', borderRadius:14, background:'linear-gradient(135deg,#7C6FF7,#9358E8)', color:'#fff', border:'none', fontSize:16, fontWeight:700, cursor:'pointer', fontFamily:'inherit', opacity:editSaving?0.6:1 }}>
                {editSaving ? 'Lagrer…' : 'Lagre endringer'}
              </button>
            </div>
          </div>
        )}

        {/* Gradient Hero Header */}
        <div style={{
          background: dark
            ? 'linear-gradient(145deg, #2D1B69 0%, #1A1245 55%, #0D0B1E 100%)'
            : 'linear-gradient(145deg, #7C6FF7 0%, #9358E8 45%, #5E4AE3 100%)',
          padding: '52px 20px 28px',
          position: 'relative',
          overflow: 'hidden',
          marginBottom: 0,
        }}>
          <div style={{ position:'absolute', top:-40, right:-40, width:160, height:160, borderRadius:'50%', background:'rgba(255,255,255,0.06)', pointerEvents:'none' }}/>
          <div style={{ position:'absolute', bottom:-60, left:-20, width:140, height:140, borderRadius:'50%', background:'rgba(255,255,255,0.04)', pointerEvents:'none' }}/>
          <div style={{ position:'relative', zIndex:1 }}>
            <div style={{ fontSize:13, color:'rgba(255,255,255,0.65)', fontWeight:500, marginBottom:2 }}>Famora</div>
            <div style={{ fontSize:24, fontWeight:800, color:'#fff', marginBottom:16 }}>Innstillinger</div>
            {/* User profile card inside hero */}
            <div style={{ background:'rgba(255,255,255,0.15)', backdropFilter:'blur(10px)', border:'1px solid rgba(255,255,255,0.25)', borderRadius:16, padding:'14px 16px', display:'flex', alignItems:'center', gap:14, cursor:'pointer' }}
              onClick={() => openMemberEdit(user)}>
              <div style={{ width:52, height:52, borderRadius:'50%', background:user?.color||'#7C6FF7', display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, fontWeight:800, color:'#fff', flexShrink:0, border:'2px solid rgba(255,255,255,0.4)' }}>
                {user?.avatar||(user?.name||'?')[0].toUpperCase()}
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:16, fontWeight:800, color:'#fff' }}>{user?.name}</div>
                <div style={{ fontSize:12, color:'rgba(255,255,255,0.7)', marginTop:2 }}>{user?.email}</div>
                <div style={{ marginTop:4, fontSize:11, fontWeight:700, color:'rgba(255,255,255,0.65)' }}>
                  {user?.role==='admin'?'👑 Administrator':'👤 Medlem'}
                </div>
              </div>
              <span style={{ color:'rgba(255,255,255,0.5)', fontSize:16 }}>›</span>
            </div>
          </div>
        </div>

        <div style={{ padding:'16px 14px 8px' }}>

          {/* Utseende */}
          <Section title="Utseende">
            <Row label="Mørkt tema" rightEl={<Toggle on={dark} onChange={toggleTheme}/>} noBorder/>
          </Section>

          {/* Varsler — kollapsbar rad */}
          <Section title="Varsler">
            <Row label="🔔 Push og e-postvarsler"
              onTap={() => setShowNotif(v => !v)}
              rightEl={<span style={{ fontSize:12, color:sub, transform: showNotif?'rotate(90deg)':'rotate(0)', transition:'transform .2s', display:'inline-block' }}>›</span>}
              noBorder={!showNotif}/>
            {showNotif && (
              <div style={{ padding:'4px 8px 12px' }}>
                <PushSection notify={(msg,type)=>setToast({msg,type:'success'})}/>
              </div>
            )}
          </Section>

          {/* Familie */}
          <Section title="Familie">
            <Row label="Familienavn" value={family?.name}/>
            <Row label="Antall medlemmer" value={`${members.length} stk`} noBorder/>
          </Section>

          {/* Familiemedlemmer — redigerbare */}
          {members.length>0 && (
            <Section title="Familiemedlemmer">
              {members.map((m,i)=>(
                <div key={m.id} onClick={() => openMemberEdit(m)}
                  style={{ display:'flex', alignItems:'center', padding:'12px 16px', borderBottom: i===members.length-1?'none':'1px solid '+(dark?'rgba(255,255,255,0.06)':'#F5F5F5'), cursor:'pointer', gap:12 }}>
                  <div style={{ width:38, height:38, borderRadius:'50%', background:m.color||'#7C6FF7', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, fontWeight:800, color:'#fff', flexShrink:0 }}>
                    {m.avatar||(m.name||'?')[0].toUpperCase()}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:15, fontWeight:600, color:txt }}>{m.name}</div>
                    <div style={{ fontSize:12, color:sub, marginTop:1 }}>{m.role==='admin'?'👑 Admin':'👤 Medlem'}{m.birth_date?` · ${new Date(m.birth_date).getFullYear()}`:''}</div>
                  </div>
                  <span style={{ color:sub, fontSize:16 }}>›</span>
                </div>
              ))}
            </Section>
          )}

          {/* Konto-handlinger */}
          <Section title="Konto">
            <Row label="Logg ut" onTap={()=>{if(window.confirm('Logg ut?'))logout();}}
              rightEl={<span style={{ color:'#7C6FF7' }}>→</span>}/>
            <Row noBorder
              label={<span style={{ color:'#F7605C' }}>🗑 Slett min konto</span>}
              onTap={()=>{ window.location.href='/slett-konto'; }}
              rightEl={<span style={{ color:'#F7605C' }}>›</span>}/>
          </Section>

          {/* Alle innstillinger */}
          <div style={{ padding:'8px 0 8px', textAlign:'center' }}>
            <button onClick={() => setShowFull(v => !v)}
              style={{ background:'none', border:'1px solid '+(dark?'rgba(255,255,255,0.12)':'rgba(124,111,247,0.2)'), borderRadius:12, padding:'10px 24px', fontSize:14, color:sub, cursor:'pointer', fontWeight:600 }}>
              {showFull ? 'Skjul innstillinger ↑' : 'Alle innstillinger ↓'}
            </button>
          </div>

          {/* Full settings (desktop-widgeter) */}
          {showFull && (
            <div style={{ marginTop:8 }}>
              {ALL_IDS.map(id => renderWidget(id))}
            </div>
          )}

          {/* Versjon */}
          <div style={{ textAlign:'center', padding:'16px 0 24px' }}>
            <div style={{ fontSize:11, color:sub, fontWeight:500 }}>Famora v{APP_VERSION} · {APP_BUILD}</div>
            <a href="https://famora.no/oppdateringer" target="_blank" rel="noopener" style={{ fontSize:11, color:'#9D98E8', textDecoration:'none' }}>Hva er nytt?</a>
            <div style={{ marginTop:10 }}>
              <button
                onClick={clearCache}
                disabled={clearingCache}
                style={{ fontSize:11, color:sub, background:'none', border:'1px solid '+(dark?'rgba(255,255,255,0.1)':'rgba(124,111,247,0.2)'), borderRadius:8, padding:'5px 14px', cursor:'pointer', fontFamily:'inherit' }}>
                {clearingCache ? '⏳ Tømmer…' : '🔄 Tøm cache og last på nytt'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1000 }}>
      <Toast msg={toast.msg} type={toast.type} />

      {wide ? (
        <div style={{ display:'flex', gap:14, alignItems:'flex-start' }}>
          <div style={{ flex:1, display:'flex', flexDirection:'column', gap:14 }}>
            {visibleOrder.filter((_, i) => i % 2 === 0).map(id => renderWidget(id))}
          </div>
          <div style={{ flex:1, display:'flex', flexDirection:'column', gap:14 }}>
            {visibleOrder.filter((_, i) => i % 2 !== 0).map(id => renderWidget(id))}
          </div>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
          {visibleOrder.map(id => renderWidget(id))}
        </div>
      )}

      {/* ── Versjon ── */}
      <div style={{ textAlign:'center', padding:'20px 0 8px', fontSize:12, color:'var(--txl)' }}>
        Famora v{APP_VERSION} · {APP_BUILD} ·{' '}
        <a href="https://famora.no/oppdateringer" target="_blank" rel="noopener" style={{ color:'var(--accent)', textDecoration:'none' }}>Hva er nytt?</a>
        <div style={{ marginTop:8 }}>
          <button
            onClick={clearCache}
            disabled={clearingCache}
            style={{ fontSize:11, color:'var(--txl)', background:'none', border:'1px solid var(--brd)', borderRadius:8, padding:'5px 14px', cursor:'pointer', fontFamily:'inherit' }}>
            {clearingCache ? '⏳ Tømmer…' : '🔄 Tøm cache og last på nytt'}
          </button>
        </div>
      </div>

      {/* ── Gjenopprett-rad ── */}
      {(hiddenAccessible.length > 0 || widgetOrder.join() !== ALL_IDS.join()) && (
        <div style={s.restoreBar}>
          {hiddenAccessible.length > 0 && (
            <>
              <span style={s.restoreLabel}>Skjulte widgeter:</span>
              {hiddenAccessible.map(id => (
                <button key={id} style={s.restoreChip} onClick={() => restoreWidget(id)}>
                  {LABELS[id]?.icon} {LABELS[id]?.title} <span style={{ opacity:.6, marginLeft:3 }}>+</span>
                </button>
              ))}
            </>
          )}
          <button style={s.resetBtn} onClick={resetLayout} title="Tilbakestill til standard">↺ Tilbakestill</button>
        </div>
      )}
    </div>
  );
}

// ─── Profil-seksjon ──────────────────────────────────────────
function ProfileSection({ user, notify, loginWithToken, logout }) {
  const [form, setForm]     = useState({ name:'', avatar:'', color:'', birth_date:'' });
  const [pwForm, setPwForm] = useState({ current:'', next:'', confirm:'' });
  const [showPw, setShowPw] = useState(false);
  const [photo, setPhoto]   = useState(null);

  useEffect(() => {
    if (user) {
      setForm({ name: user.name||'', avatar: user.avatar||'', color: user.color||'#7C6FF7', birth_date: user.birth_date||'' });
      setPhoto(user.photo||null);
    }
  }, [user]);

  const saveProfile = async e => {
    e.preventDefault();
    try {
      await api.put('/api/auth/profile', { ...form, photo });
      notify('Profil oppdatert!');
    } catch(ex) { notify(ex.response?.data?.error || 'Feil', 'error'); }
  };

  const changePassword = async e => {
    e.preventDefault();
    if (pwForm.next !== pwForm.confirm) return notify('Passordene er ikke like', 'error');
    try {
      await api.put('/api/auth/password', { current: pwForm.current, next: pwForm.next });
      notify('Passord endret!');
      setPwForm({ current:'', next:'', confirm:'' });
      setShowPw(false);
    } catch(ex) { notify(ex.response?.data?.error || 'Feil', 'error'); }
  };

  return (
    <div style={s.body}>
      <form onSubmit={saveProfile} style={s.form}>
        <PhotoUpload photo={photo} onPhoto={setPhoto} size={80} label="Endre profilbilde" />
        <div style={s.avatarPicker}>
          <div style={s.avatarNote}>Emoji brukes som reservebilde hvis foto mangler</div>
          <Field label="Emoji-avatar">
            <div style={s.emojiGrid}>
              {AVATARS.map(a => (
                <button key={a} type="button"
                  style={{...s.emojiBtn, ...(form.avatar===a ? s.emojiBtnActive : {})}}
                  onClick={() => setForm(f=>({...f,avatar:a}))}>{a}</button>
              ))}
            </div>
          </Field>
        </div>
        <Field label="Farge">
          <div style={s.colorRow}>
            {COLORS.map(c => (
              <div key={c} onClick={() => setForm(f=>({...f,color:c}))}
                style={{ width:28, height:28, borderRadius:'50%', background:c, cursor:'pointer',
                  border: form.color===c ? '3px solid var(--txt)' : '3px solid transparent' }} />
            ))}
          </div>
        </Field>
        <div style={s.twoCol}>
          <Field label="Navn">
            <input style={s.inp} value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} required />
          </Field>
          <Field label="Fødselsdato">
            <input style={s.inp} type="date" value={form.birth_date} onChange={e=>setForm(f=>({...f,birth_date:e.target.value}))} />
          </Field>
        </div>
        <div style={s.infoRow}><span style={s.infoLbl}>E-post</span><span style={s.infoVal}>{user?.email}</span></div>
        <div style={s.infoRow}><span style={s.infoLbl}>Rolle</span><span style={s.infoVal}>{user?.role==='admin'?'👑 Administrator':'Familiemedlem'}</span></div>
        <button style={s.saveBtn} type="submit">Lagre profil</button>
      </form>
      <div style={s.btnRow}>
        <button style={s.outlineBtn} onClick={() => setShowPw(v=>!v)}>
          {showPw ? 'Avbryt passordendring' : '🔑 Endre passord'}
        </button>
        <a href="/slett-konto" style={{ ...s.outlineBtn, color:'var(--red)', borderColor:'var(--red)', textDecoration:'none', display:'inline-flex', alignItems:'center' }}>
          🗑 Slett konto
        </a>
        <button style={{...s.outlineBtn, color:'#F7605C', borderColor:'#FCA5A5'}}
          onClick={async () => {
            if (!window.confirm('Meld deg ut av familien? Du vil miste tilgang til appen.')) return;
            try { await api.delete('/api/auth/leave'); logout(); }
            catch(ex) { notify(ex.response?.data?.error || 'Feil', 'error'); }
          }}>
          🚪 Meld meg ut
        </button>
      </div>
      {showPw && (
        <form onSubmit={changePassword} style={{...s.form, marginTop:12}}>
          <Field label="Nåværende passord">
            <input style={s.inp} type="password" value={pwForm.current} onChange={e=>setPwForm(f=>({...f,current:e.target.value}))} required />
          </Field>
          <div style={s.twoCol}>
            <Field label="Nytt passord">
              <input style={s.inp} type="password" value={pwForm.next} onChange={e=>setPwForm(f=>({...f,next:e.target.value}))} required minLength={8} />
            </Field>
            <Field label="Bekreft">
              <input style={s.inp} type="password" value={pwForm.confirm} onChange={e=>setPwForm(f=>({...f,confirm:e.target.value}))} required />
            </Field>
          </div>
          <button style={s.saveBtn} type="submit">Lagre passord</button>
        </form>
      )}
    </div>
  );
}

// ─── Familie-seksjon ─────────────────────────────────────────
function FamilySection({ family, members, user, notify, refreshMembers }) {
  const [editing, setEditing] = useState(false);
  const [famName, setFamName] = useState('');

  useEffect(() => { if (family) setFamName(family.name||''); }, [family]);

  const saveFamName = async e => {
    e.preventDefault();
    try {
      await api.put('/api/auth/family', { name: famName });
      notify('Familienavn oppdatert!');
      setEditing(false);
    } catch(ex) { notify(ex.response?.data?.error||'Feil','error'); }
  };

  return (
    <div style={s.body}>
      <div style={s.famNameRow}>
        {editing ? (
          <form onSubmit={saveFamName} style={{display:'flex',gap:8,flex:1}}>
            <input style={{...s.inp,flex:1}} value={famName} onChange={e=>setFamName(e.target.value)} required autoFocus />
            <button style={s.saveBtn} type="submit">Lagre</button>
            <button style={s.cancelBtn} type="button" onClick={()=>setEditing(false)}>Avbryt</button>
          </form>
        ) : (
          <>
            <span style={s.famName}>{family?.name}</span>
            {user?.role==='admin' && <button style={s.editBtn} onClick={()=>setEditing(true)}>✏️ Endre</button>}
          </>
        )}
      </div>
      <div style={s.memberGrid}>
        {members.map(m => (
          <div key={m.id} style={s.memberCard}>
            <div style={{...s.memberAvatar, background:m.color||'#7C6FF7'}}>{m.avatar||'?'}</div>
            <div style={s.memberName}>{m.name}</div>
            <div style={s.memberRole}>{m.role==='admin'?'👑 Admin':'Medlem'}</div>
            {m.birth_date && <div style={s.memberBirth}>🎂 {new Date(m.birth_date).toLocaleDateString('nb-NO',{day:'numeric',month:'long'})}</div>}
            {m.last_seen  && <div style={s.memberSeen}>Sett {new Date(m.last_seen).toLocaleDateString('nb-NO',{day:'numeric',month:'short'})}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Inviter-seksjon ─────────────────────────────────────────
function InviteSection({ notify, refreshMembers }) {
  const [mode, setMode] = useState(null);
  const [adultForm, setAdultForm] = useState({ name:'', email:'', color:'#7C6FF7' });
  const setA = k => e => setAdultForm(f=>({...f,[k]:e.target.value}));
  const saveAdult = async e => {
    e.preventDefault();
    try {
      const res = await api.post('/api/auth/invite', adultForm);
      const emailMsg = res?.emailSent ? ' — invitasjon sendt på e-post!' : ' (e-post ikke sendt)';
      notify(`✅ ${adultForm.name} er lagt til${emailMsg}`);
      setAdultForm({ name:'', email:'', color:'#7C6FF7' });
      setMode(null); refreshMembers();
    } catch(ex) { notify(ex.response?.data?.error||'Feil','error'); }
  };
  const [childForm, setChildForm] = useState({ name:'', birth_date:'', color:'#34D399', avatar:'' });
  const setC = k => e => setChildForm(f=>({...f,[k]:e.target.value}));
  const saveChild = async e => {
    e.preventDefault();
    try {
      await api.post('/api/auth/add-child', childForm);
      notify(`✅ ${childForm.name} er lagt til!`);
      setChildForm({ name:'', birth_date:'', color:'#34D399', avatar:'' });
      setMode(null); refreshMembers();
    } catch(ex) { notify(ex.response?.data?.error||'Feil','error'); }
  };

  return (
    <div style={s.body}>
      {!mode && (
        <div style={{display:'flex', flexDirection:'column', gap:8}}>
          <button style={s.inviteTypeBtn} onClick={()=>setMode('adult')}>
            <span style={{fontSize:20}}>👤</span>
            <div style={{flex:1, textAlign:'left'}}>
              <div style={{fontWeight:700, color:'var(--txt)', fontSize:14}}>Inviter voksen med e-post</div>
              <div style={{fontSize:12, color:'var(--txl)', marginTop:1}}>Personen får innlogging og e-postinvitasjon</div>
            </div>
            <span style={{color:'var(--txl)'}}>→</span>
          </button>
          <button style={s.inviteTypeBtn} onClick={()=>setMode('child')}>
            <span style={{fontSize:20}}>🧒</span>
            <div style={{flex:1, textAlign:'left'}}>
              <div style={{fontWeight:700, color:'var(--txt)', fontSize:14}}>Legg til barn (uten innlogging)</div>
              <div style={{fontSize:12, color:'var(--txl)', marginTop:1}}>Barn vises i appen uten eget passord</div>
            </div>
            <span style={{color:'var(--txl)'}}>→</span>
          </button>
        </div>
      )}
      {mode === 'adult' && (
        <>
          <div style={s.modeHeader}>
            <button style={s.backLink} onClick={()=>setMode(null)}>← Tilbake</button>
            <span style={{fontSize:13, fontWeight:700, color:'var(--txt)'}}>Inviter voksen</span>
          </div>
          <div style={{fontSize:12, color:'var(--txl)', marginBottom:8, lineHeight:1.5}}>
            Et tilfeldig passord genereres automatisk og sendes til e-postadressen.
          </div>
          <form onSubmit={saveAdult} style={s.form}>
            <div style={s.twoCol}>
              <Field label="Navn"><input style={s.inp} value={adultForm.name} onChange={setA('name')} required /></Field>
              <Field label="E-post"><input style={s.inp} type="email" value={adultForm.email} onChange={setA('email')} required /></Field>
            </div>
            <Field label="Farge">
              <div style={s.colorRow}>
                {COLORS.map(c=>(
                  <div key={c} onClick={()=>setAdultForm(f=>({...f,color:c}))}
                    style={{width:26,height:26,borderRadius:'50%',background:c,cursor:'pointer',
                      border:adultForm.color===c?'3px solid var(--txt)':'3px solid transparent'}} />
                ))}
              </div>
            </Field>
            <button style={s.saveBtn} type="submit">Inviter og send e-post</button>
          </form>
        </>
      )}
      {mode === 'child' && (
        <>
          <div style={s.modeHeader}>
            <button style={s.backLink} onClick={()=>setMode(null)}>← Tilbake</button>
            <span style={{fontSize:13, fontWeight:700, color:'var(--txt)'}}>Legg til barn</span>
          </div>
          <div style={{fontSize:12, color:'var(--txl)', marginBottom:8, lineHeight:1.5}}>
            Barn legges til uten e-post og passord — kun synlig i appen.
          </div>
          <form onSubmit={saveChild} style={s.form}>
            <Field label="Navn *"><input style={s.inp} value={childForm.name} onChange={setC('name')} required autoFocus /></Field>
            <Field label="Fødselsdato"><input style={s.inp} type="date" value={childForm.birth_date} onChange={setC('birth_date')} /></Field>
            <Field label="Emoji-avatar">
              <div style={s.emojiGrid}>
                {AVATARS.map(a=>(
                  <button key={a} type="button"
                    style={{...s.emojiBtn,...(childForm.avatar===a?s.emojiBtnActive:{})}}
                    onClick={()=>setChildForm(f=>({...f,avatar:a}))}>{a}</button>
                ))}
              </div>
            </Field>
            <Field label="Farge">
              <div style={s.colorRow}>
                {COLORS.map(c=>(
                  <div key={c} onClick={()=>setChildForm(f=>({...f,color:c}))}
                    style={{width:26,height:26,borderRadius:'50%',background:c,cursor:'pointer',
                      border:childForm.color===c?'3px solid var(--txt)':'3px solid transparent'}} />
                ))}
              </div>
            </Field>
            <button style={s.saveBtn} type="submit">Legg til barn</button>
          </form>
        </>
      )}
    </div>
  );
}

// ─── Admin-seksjon ───────────────────────────────────────────
function AdminMembersSection({ members, user, notify, refreshMembers }) {
  const [expanded, setExpanded] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [pwForm, setPwForm]     = useState({});

  const toggle = (id) => setExpanded(e => e === id ? null : id);

  const changeRole = async (id, role) => {
    try { await api.put(`/api/auth/members/${id}/role`, { role }); notify('Rolle oppdatert'); refreshMembers(); }
    catch(ex) { notify(ex.response?.data?.error||'Feil','error'); }
  };
  const makeAdmin = async (id, name) => {
    if (!window.confirm(`Gi ${name} administrator-rollen?`)) return;
    try { await api.put(`/api/auth/members/${id}/make-admin`, {}); notify(`${name} er nå admin`); refreshMembers(); }
    catch(ex) { notify(ex.response?.data?.error||'Feil','error'); }
  };
  const toggleActive = async (id, name, active) => {
    if (!window.confirm(active ? `Reaktiver ${name}?` : `Deaktiver ${name}?`)) return;
    try { await api.put(`/api/auth/members/${id}/active`, { active }); notify(active ? `${name} reaktivert` : `${name} deaktivert`); refreshMembers(); }
    catch(ex) { notify(ex.response?.data?.error||'Feil','error'); }
  };
  const removeMember = async (id, name) => {
    if (!window.confirm(`Fjerne ${name} fra familien permanent?`)) return;
    try { await api.delete(`/api/auth/members/${id}`); notify(`${name} er fjernet`); refreshMembers(); }
    catch(ex) { notify(ex.response?.data?.error||'Feil','error'); }
  };
  const resetPassword = async (id, e) => {
    e.preventDefault();
    const pw = pwForm[id];
    if (!pw || pw.length < 8) return notify('Passord må være minst 8 tegn','error');
    try { await api.put(`/api/auth/members/${id}/password`, { password: pw }); notify('Passord tilbakestilt'); setPwForm(f=>({...f,[id]:''})); }
    catch(ex) { notify(ex.response?.data?.error||'Feil','error'); }
  };
  const saveProfile = async (id, e) => {
    e.preventDefault();
    const f = editForm[id] || {};
    try { await api.put(`/api/auth/members/${id}/profile`, { ...f, photo: f.photo||null }); notify('Profil oppdatert'); refreshMembers(); }
    catch(ex) { notify(ex.response?.data?.error||'Feil','error'); }
  };
  const initEdit = (m) => setEditForm(f => ({
    ...f,
    [m.id]: { name: m.name, avatar: m.avatar||'', color: m.color||'#7C6FF7', birth_date: m.birth_date||'', photo: m.photo||null }
  }));

  const others = members.filter(m => m.id !== user?.id);
  if (others.length === 0) return <div style={{...s.body, color:'var(--txl)', fontSize:13}}>Ingen andre medlemmer å administrere.</div>;

  return (
    <div style={s.body}>
      {others.map(m => {
        const ef = editForm[m.id];
        const isActive = m.active !== 0;
        return (
          <div key={m.id} style={{...s.adminRow, ...(isActive?{}:s.adminRowInactive)}}>
            <div style={s.adminTop}>
              <div style={{...s.memberAvatar, width:40, height:40, background:m.color||'#7C6FF7', fontSize:16, opacity:isActive?1:.5}}>{m.avatar||'?'}</div>
              <div style={s.adminInfo}>
                <div style={s.adminName}>{m.name} {!isActive && <span style={s.inactiveBadge}>Deaktivert</span>}</div>
                <div style={s.adminEmail}>{m.email} · {m.role==='admin'?'👑 Admin':'Medlem'}</div>
              </div>
              <button style={s.expandBtn} onClick={() => { toggle(m.id); initEdit(m); }}>
                {expanded===m.id ? '▲ Lukk' : '▼ Rediger'}
              </button>
            </div>
            {expanded===m.id && (
              <div style={s.expandedPanel}>
                <div style={s.subSection}>
                  <div style={s.subTitle}>✏️ Rediger profil</div>
                  {ef && (
                    <form onSubmit={e=>saveProfile(m.id,e)} style={s.form}>
                      <PhotoUpload photo={ef.photo||null} size={64}
                        onPhoto={p=>setEditForm(f=>({...f,[m.id]:{...f[m.id],photo:p}}))} />
                      <div style={s.twoCol}>
                        <div style={s.field}>
                          <label style={s.lbl}>Navn</label>
                          <input style={s.inp} value={ef.name||''} onChange={e=>setEditForm(f=>({...f,[m.id]:{...f[m.id],name:e.target.value}}))} required />
                        </div>
                        <div style={s.field}>
                          <label style={s.lbl}>Fødselsdato</label>
                          <input style={s.inp} type="date" value={ef.birth_date||''} onChange={e=>setEditForm(f=>({...f,[m.id]:{...f[m.id],birth_date:e.target.value}}))} />
                        </div>
                      </div>
                      <div style={s.field}>
                        <label style={s.lbl}>Avatar</label>
                        <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
                          {AVATARS.map(a=>(
                            <button key={a} type="button"
                              style={{...s.emojiBtn,...(ef.avatar===a?s.emojiBtnActive:{})}}
                              onClick={()=>setEditForm(f=>({...f,[m.id]:{...f[m.id],avatar:a}}))}>
                              {a}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div style={s.field}>
                        <label style={s.lbl}>Farge</label>
                        <div style={s.colorRow}>
                          {COLORS.map(c=>(
                            <div key={c} onClick={()=>setEditForm(f=>({...f,[m.id]:{...f[m.id],color:c}}))}
                              style={{width:26,height:26,borderRadius:'50%',background:c,cursor:'pointer',
                                border:ef.color===c?'3px solid var(--txt)':'3px solid transparent'}} />
                          ))}
                        </div>
                      </div>
                      <button style={s.saveBtn} type="submit">Lagre profil</button>
                    </form>
                  )}
                </div>
                <div style={s.subSection}>
                  <div style={s.subTitle}>🔑 Tilbakestill passord</div>
                  <form onSubmit={e=>resetPassword(m.id,e)} style={{display:'flex',gap:8}}>
                    <input style={{...s.inp,flex:1}} type="password" placeholder="Nytt passord (min. 8 tegn)"
                      value={pwForm[m.id]||''} onChange={e=>setPwForm(f=>({...f,[m.id]:e.target.value}))} minLength={8} />
                    <button style={s.saveBtn} type="submit">Sett</button>
                  </form>
                </div>
                <div style={s.subSection}>
                  <div style={s.subTitle}>🛡 Rolle og tilgang</div>
                  <div style={s.actionGrid}>
                    <select style={s.roleSelect} value={m.role} onChange={e=>changeRole(m.id,e.target.value)}>
                      <option value="member">Medlem</option>
                      <option value="admin">👑 Administrator</option>
                    </select>
                    {m.role !== 'admin' && (
                      <button style={s.actionBtn} onClick={()=>makeAdmin(m.id,m.name)}>👑 Gjør til admin</button>
                    )}
                    <button style={{...s.actionBtn,...(isActive?s.warnBtn:s.okBtn)}}
                      onClick={()=>toggleActive(m.id,m.name,!isActive)}>
                      {isActive ? '🚫 Deaktiver konto' : '✅ Reaktiver konto'}
                    </button>
                    <button style={{...s.actionBtn,...s.dangerBtn}} onClick={()=>removeMember(m.id,m.name)}>
                      🗑 Fjern fra familie
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Lokasjon-seksjon ────────────────────────────────────────
function LocationSection({ famSettings, notify, refresh }) {
  const [form, setForm]           = useState({ home_name:'', home_lat:'', home_lon:'' });
  const [query, setQuery]         = useState('');
  const [results, setResults]     = useState([]);
  const [searching, setSearching] = useState(false);
  const [gpsLoading, setGpsLoading] = useState(false);

  useEffect(() => {
    if (famSettings) {
      setForm({ home_name: famSettings.home_name||'', home_lat: famSettings.home_lat!=null?famSettings.home_lat:'', home_lon: famSettings.home_lon!=null?famSettings.home_lon:'' });
      if (famSettings.home_name) setQuery(famSettings.home_name);
    }
  }, [famSettings]);

  const search = async () => {
    if (!query.trim()) return;
    setSearching(true); setResults([]);
    try { const data = await api.get(`/api/geocode/search?q=${encodeURIComponent(query)}`); setResults(data); }
    catch { notify('Søk feilet','error'); }
    finally { setSearching(false); }
  };
  const pickResult = r => {
    setForm({ home_name: r.display_name.split(',').slice(0,2).join(',').trim(), home_lat: r.lat, home_lon: r.lon });
    setQuery(r.display_name.split(',').slice(0,2).join(',').trim());
    setResults([]);
  };
  const useGps = () => {
    if (!navigator.geolocation) return notify('GPS ikke støttet av nettleseren','error');
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(async pos => {
      try {
        const { data } = await api.get(`/api/geocode/reverse?lat=${pos.coords.latitude}&lon=${pos.coords.longitude}`)
          .then(d => ({ data: d })).catch(() => ({ data: null }));
        const name = data?.short_name || 'Min posisjon';
        setForm({ home_name: name, home_lat: pos.coords.latitude, home_lon: pos.coords.longitude });
        setQuery(name);
        notify(`📍 Fant posisjon: ${name}`);
      } finally { setGpsLoading(false); }
    }, err => { setGpsLoading(false); notify('Kunne ikke hente posisjon: ' + err.message, 'error'); },
    { enableHighAccuracy: true, timeout: 10000 });
  };
  const save = async e => {
    e.preventDefault();
    try {
      await api.put('/api/family/settings', { home_name: form.home_name||null, home_lat: form.home_lat!==''?parseFloat(form.home_lat):null, home_lon: form.home_lon!==''?parseFloat(form.home_lon):null });
      notify('Hjemsted lagret!'); refresh();
    } catch { notify('Noe gikk galt','error'); }
  };

  return (
    <div style={s.body}>
      <p style={s.hint}>Søk etter by/adresse eller bruk GPS. Brukes til værvarsell på hjemskjermen.</p>
      <div style={s.locSearch}>
        <input style={{...s.inp, flex:1}} placeholder="Søk etter by eller adresse…"
          value={query} onChange={e=>setQuery(e.target.value)}
          onKeyDown={e=>e.key==='Enter'&&(e.preventDefault(),search())} />
        <button style={s.saveBtn} type="button" onClick={search} disabled={searching}>{searching ? '…' : '🔍 Søk'}</button>
        <button style={s.outlineBtn} type="button" onClick={useGps} disabled={gpsLoading}>{gpsLoading ? '…' : '📍 GPS'}</button>
      </div>
      {results.length > 0 && (
        <div style={s.locResults}>
          {results.map((r,i) => (
            <button key={i} style={s.locResult} onClick={() => pickResult(r)}>
              <span style={s.locResultName}>{r.display_name.split(',').slice(0,3).join(', ')}</span>
            </button>
          ))}
        </div>
      )}
      {form.home_lat !== '' && (
        <div style={s.locChosen}>
          <span style={s.locChosenIcon}>✅</span>
          <div>
            <div style={s.locChosenName}>{form.home_name}</div>
            <div style={s.locChosenCoords}>{typeof form.home_lat==='number'?form.home_lat.toFixed(4):form.home_lat}, {typeof form.home_lon==='number'?form.home_lon.toFixed(4):form.home_lon}</div>
          </div>
        </div>
      )}
      <form onSubmit={save}>
        <button style={s.saveBtn} type="submit" disabled={form.home_lat===''}>Lagre hjemsted</button>
      </form>
    </div>
  );
}

// ─── Nyhetskilder-seksjon ────────────────────────────────────
const PRESET_FEEDS = [
  { name: 'NRK',          url: 'https://www.nrk.no/toppsaker.rss' },
  { name: 'VG',           url: 'https://www.vg.no/rss/feed/' },
  { name: 'Dagbladet',    url: 'https://www.dagbladet.no/rss/' },
  { name: 'E24',          url: 'https://e24.no/rss/seksjon/forside/' },
  { name: 'Nettavisen',   url: 'https://www.nettavisen.no/rss/nyheter.rss' },
  { name: 'Finansavisen', url: 'https://finansavisen.no/rss' },
  { name: 'Digi.no',      url: 'https://www.digi.no/rss/alle' },
  { name: 'Tek.no',       url: 'https://www.tek.no/rss' },
];

function NewsSection({ famSettings, notify, refresh }) {
  const [feeds, setFeeds]           = useState([]);
  const [customUrl, setCustomUrl]   = useState('');
  const [customName, setCustomName] = useState('');
  const [testing, setTesting]       = useState(false);
  const [saving, setSaving]         = useState(false);

  useEffect(() => { if (famSettings?.rss_feeds) setFeeds(famSettings.rss_feeds); }, [famSettings]);

  const save = async (newFeeds) => {
    setSaving(true);
    try { await api.put('/api/family/settings', { rss_feeds: newFeeds }); setFeeds(newFeeds); refresh(); notify('Nyhetskilder lagret!'); }
    catch { notify('Lagring feilet', 'error'); }
    finally { setSaving(false); }
  };
  const togglePreset = (preset) => {
    const existing = feeds.find(f => f.url === preset.url);
    save(existing
      ? feeds.map(f => f.url === preset.url ? { ...f, active: !f.active } : f)
      : [...feeds, { url: preset.url, name: preset.name, active: true, custom: false }]);
  };
  const removeCustom = (url) => save(feeds.filter(f => f.url !== url));
  const addCustom = async () => {
    const url  = customUrl.trim();
    const name = customName.trim();
    if (!url) return notify('Lim inn en RSS-URL', 'error');
    if (feeds.find(f => f.url === url)) return notify('Denne kilden er allerede lagt til', 'error');
    setTesting(true);
    try {
      const res = await api.get(`/api/rss/test?url=${encodeURIComponent(url)}`);
      if (!res.ok) throw new Error(res.error || 'Feil');
      const feedName = name || url.replace(/https?:\/\/(www\.)?/, '').split('/')[0];
      await save([...feeds, { url, name: feedName, active: true, custom: true }]);
      setCustomUrl(''); setCustomName('');
      notify(`✅ ${feedName} lagt til (${res.count} artikler funnet)`);
    } catch(err) { notify('Kunne ikke lese feeden: ' + err.message, 'error'); }
    finally { setTesting(false); }
  };
  const presetIsActive = (p) => feeds.find(x => x.url === p.url)?.active ?? false;
  const customFeeds = feeds.filter(f => f.custom);

  return (
    <div style={s.body}>
      <p style={s.hint}>Velg norske nyhetskilder som vises i Nyheter-widgeten på hjemskjermen. Feeds oppdateres hvert 15. minutt.</p>
      <div style={s.lbl}>Ferdige norske kilder</div>
      <div style={ns.presetGrid}>
        {PRESET_FEEDS.map(p => {
          const on = presetIsActive(p);
          return (
            <button key={p.url} style={{ ...ns.presetBtn, ...(on ? ns.presetOn : ns.presetOff) }}
              onClick={() => togglePreset(p)} disabled={saving}>
              {on ? '✓ ' : '+ '}{p.name}
            </button>
          );
        })}
      </div>
      <div style={{ ...s.lbl, marginTop:12 }}>Legg til egen kilde</div>
      <div style={ns.addRow}>
        <input style={{ ...s.inp, flex: 2 }} placeholder="https://eksempel.no/rss"
          value={customUrl} onChange={e => setCustomUrl(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && addCustom()} />
        <input style={{ ...s.inp, flex: 1 }} placeholder="Navn (valgfritt)"
          value={customName} onChange={e => setCustomName(e.target.value)} />
        <button style={s.saveBtn} onClick={addCustom} disabled={testing || saving}>
          {testing ? '…' : 'Legg til'}
        </button>
      </div>
      {customFeeds.length > 0 && (
        <div style={ns.customList}>
          <div style={{ ...s.lbl, marginBottom:6 }}>Egne kilder</div>
          {customFeeds.map(f => (
            <div key={f.url} style={ns.customRow}>
              <button style={{ ...ns.toggleSmall, ...(f.active ? ns.toggleOn : ns.toggleOff) }}
                onClick={() => save(feeds.map(x => x.url === f.url ? { ...x, active: !x.active } : x))}>
                {f.active ? '● På' : '○ Av'}
              </button>
              <div style={ns.customInfo}>
                <div style={ns.customName}>{f.name}</div>
                <div style={ns.customUrl}>{f.url}</div>
              </div>
              <button style={ns.deleteBtn} onClick={() => removeCustom(f.url)}>✕</button>
            </div>
          ))}
        </div>
      )}
      {feeds.filter(f => f.active).length > 0 && (
        <div style={ns.activeNote}>
          {feeds.filter(f => f.active).length} kilde{feeds.filter(f => f.active).length !== 1 ? 'r' : ''} aktiv{feeds.filter(f => f.active).length !== 1 ? 'e' : ''}:{' '}
          {feeds.filter(f => f.active).map(f => f.name).join(', ')}
        </div>
      )}
    </div>
  );
}

const ns = {
  presetGrid:  { display: 'flex', flexWrap: 'wrap', gap: 8 },
  presetBtn:   { padding: '7px 14px', borderRadius: 20, fontWeight: 700, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', border: 'none', transition: '.15s' },
  presetOn:    { background: '#7C6FF7', color: '#fff' },
  presetOff:   { background: 'var(--bg2)', color: 'var(--txm)' },
  addRow:      { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' },
  customList:  { display: 'flex', flexDirection: 'column', gap: 6 },
  customRow:   { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: 'var(--bg)', borderRadius: 10 },
  toggleSmall: { padding: '3px 10px', borderRadius: 12, fontWeight: 700, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit', border: 'none', flexShrink: 0 },
  toggleOn:    { background: 'var(--green-bg)', color: 'var(--green)' },
  toggleOff:   { background: 'var(--red-bg)', color: 'var(--red)' },
  customInfo:  { flex: 1, minWidth: 0 },
  customName:  { fontSize: 13, fontWeight: 700, color: 'var(--txt)' },
  customUrl:   { fontSize: 11, color: 'var(--txl)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  deleteBtn:   { background: 'none', border: 'none', color: 'var(--txl)', cursor: 'pointer', fontSize: 14, padding: '2px 4px', flexShrink: 0 },
  activeNote:  { fontSize: 12, color: 'var(--txm)', background: '#F0FDF4', border: '1px solid #86EFAC', borderRadius: 9, padding: '8px 12px' },
};

// ─── Google-seksjon ──────────────────────────────────────────
function GoogleSection({ googleStatus, refreshGoogle, notify }) {
  const disconnect = async () => {
    if (!window.confirm('Koble fra Google?')) return;
    await api.delete('/api/google/disconnect');
    refreshGoogle();
    notify('Google frakoblet');
  };

  return (
    <div style={s.body}>
      {googleStatus.connected ? (
        <>
          <div style={s.statusBadge}>✅ Koblet til Google</div>
          <div style={s.btnRow}>
            <button style={s.saveBtn} onClick={() =>
              api.post('/api/google/calendar/sync',{})
                .then(r => notify(`Synkronisert ${r.synced} nye hendelser`))
                .catch(()=>notify('Synkronisering feilet','error'))
            }>🗓 Synkroniser kalender</button>
            <a href="/api/google/auth?action=connect" style={s.outlineBtn}>🔄 Oppdater tilganger</a>
            <button style={{...s.outlineBtn, color:'#F7605C', borderColor:'#F7605C'}} onClick={disconnect}>Koble fra</button>
          </div>
          <div style={s.scopeInfo}><strong>Aktive tilganger:</strong> Profil · E-post · Google Kalender · Gmail</div>
        </>
      ) : (
        <>
          <p style={s.hint}>Koble til Google for å synkronisere kalender og lese Gmail i appen.</p>
          <a href="/api/google/auth?action=connect" style={s.saveBtn}>Koble til Google</a>
        </>
      )}
    </div>
  );
}

// ─── Push-varsler-seksjon ────────────────────────────────────
function PushSection({ notify }) {
  const [status,     setStatus]     = useState('ukjent');
  const [emailNotif, setEmailNotif] = useState(false);
  const [emailSaving, setEmailSaving] = useState(false);

  useEffect(() => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) { setStatus('støttes ikke'); return; }
    navigator.serviceWorker.ready.then(reg =>
      reg.pushManager.getSubscription().then(sub => setStatus(sub ? 'aktivert' : 'deaktivert'))
    );
    api.get('/api/auth/notification-prefs')
      .then(r => setEmailNotif(!!r?.email_notifications))
      .catch(() => {});
  }, []);

  const enable = async () => {
    try {
      const reg = await navigator.serviceWorker.register('/sw.js');
      const vapidRes = await api.get('/api/push/vapid-public-key');
      if (!vapidRes?.key) return notify('VAPID-nøkkel mangler','error');
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') return notify('Varsler ble ikke godkjent','error');
      const padding = '='.repeat((4 - vapidRes.key.length%4)%4);
      const raw = window.atob((vapidRes.key+padding).replace(/-/g,'+').replace(/_/g,'/'));
      const key = Uint8Array.from([...raw].map(c=>c.charCodeAt(0)));
      const sub = await reg.pushManager.subscribe({ userVisibleOnly:true, applicationServerKey:key });
      await api.post('/api/push/subscribe', { subscription: sub });
      setStatus('aktivert');
      notify('Push-varsler aktivert!');
    } catch(ex) { notify('Kunne ikke aktivere varsler: '+ex.message,'error'); }
  };

  const disable = async () => {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) { await api.delete('/api/push/subscribe', { endpoint: sub.endpoint }).catch(()=>{}); await sub.unsubscribe(); }
    setStatus('deaktivert');
    notify('Push-varsler deaktivert');
  };

  const toggleEmail = async () => {
    setEmailSaving(true);
    const next = !emailNotif;
    try {
      await api.put('/api/auth/notification-prefs', { email_notifications: next ? 1 : 0 });
      setEmailNotif(next);
      notify(next ? 'E-postvarsler aktivert' : 'E-postvarsler deaktivert');
    } catch { notify('Kunne ikke lagre','error'); }
    setEmailSaving(false);
  };

  const Toggle = ({ on, onChange, disabled }) => (
    <button type="button" onClick={onChange} disabled={disabled} style={{
      position:'relative', width:48, height:26, borderRadius:13, border:'none', cursor:'pointer',
      background: on ? '#7C6FF7' : 'var(--bdr)', transition:'background .2s', flexShrink:0,
      opacity: disabled ? 0.6 : 1,
    }}>
      <span style={{
        position:'absolute', top:3, left: on ? 24 : 3,
        width:20, height:20, borderRadius:'50%', background:'#fff',
        boxShadow:'0 1px 4px rgba(0,0,0,.2)', transition:'left .2s', display:'block',
      }}/>
    </button>
  );

  return (
    <div style={s.body}>
      {/* Push-varsler */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
        <div>
          <div style={{ fontSize:14, fontWeight:700, color:'var(--txt)', marginBottom:2 }}>Push-varsler</div>
          <div style={{ fontSize:12, color:'var(--txl)' }}>Varsler i nettleseren (mobil og PC)</div>
        </div>
        {status === 'aktivert' ? (
          <Toggle on={true} onChange={disable} />
        ) : status === 'deaktivert' ? (
          <Toggle on={false} onChange={enable} />
        ) : (
          <span style={{ fontSize:12, color:'var(--txl)' }}>{status === 'støttes ikke' ? 'Støttes ikke' : '…'}</span>
        )}
      </div>

      {status === 'aktivert' && (
        <div style={{ fontSize:12, color:'var(--green)', background:'var(--green-bg)', borderRadius:8, padding:'6px 10px', marginBottom:10 }}>
          ✓ Push-varsler er aktive — du mottar varsler for chat og kalender
        </div>
      )}

      {/* E-postvarsler */}
      <div style={{ height:1, background:'var(--bdr)', margin:'8px 0' }}/>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div>
          <div style={{ fontSize:14, fontWeight:700, color:'var(--txt)', marginBottom:2 }}>E-postvarsler</div>
          <div style={{ fontSize:12, color:'var(--txl)' }}>Motta e-post ved nye hendelser, meldinger og oppgaver</div>
        </div>
        <Toggle on={emailNotif} onChange={toggleEmail} disabled={emailSaving} />
      </div>
      {emailNotif && (
        <div style={{ fontSize:12, color:'var(--green)', background:'var(--green-bg)', borderRadius:8, padding:'6px 10px', marginTop:8 }}>
          ✓ E-postvarsler er aktive — sendes fra noreply@famora.no
        </div>
      )}
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────
const s = {
  widget:        { background:'var(--card)', borderRadius:16, border:'1px solid var(--bdr)', overflow:'hidden' },
  widgetHeader:  { display:'flex', alignItems:'center', gap:10, padding:'12px 16px', borderBottom:'1px solid var(--bdr)', userSelect:'none' },
  dragHandle:    { fontSize:16, color:'var(--txl)', cursor:'grab', padding:'0 4px', lineHeight:1, flexShrink:0 },
  widgetIcon:    { fontSize:16, flexShrink:0 },
  widgetTitle:   { fontSize:13, fontWeight:800, color:'var(--txt)', flex:1, letterSpacing:.1 },
  closeBtn:      { background:'none', border:'none', color:'var(--txl)', cursor:'pointer', fontSize:13, padding:'2px 5px', borderRadius:6, lineHeight:1, flexShrink:0, fontFamily:'inherit' },
  toast:         { padding:'12px 16px', borderRadius:12, fontSize:13, fontWeight:600, marginBottom:14 },
  body:          { padding:'16px 18px', display:'flex', flexDirection:'column', gap:12 },
  form:          { display:'flex', flexDirection:'column', gap:10 },
  twoCol:        { display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 },
  field:         { display:'flex', flexDirection:'column', gap:5 },
  lbl:           { fontSize:11, fontWeight:700, color:'var(--txm)', textTransform:'uppercase', letterSpacing:.5 },
  inp:           { padding:'10px 12px', borderRadius:10, border:'1px solid var(--input-bdr)', fontFamily:'inherit', fontSize:14, color:'var(--input-txt)', outline:'none', background:'var(--input-bg)' },
  saveBtn:       { padding:'11px 20px', borderRadius:11, border:'none', background:'#7C6FF7', color:'#fff', fontWeight:700, cursor:'pointer', fontSize:14, fontFamily:'inherit', display:'inline-block', textDecoration:'none', textAlign:'center' },
  cancelBtn:     { padding:'11px 14px', borderRadius:11, border:'1px solid var(--bdr)', background:'var(--card)', color:'var(--txm)', fontWeight:600, cursor:'pointer', fontSize:14, fontFamily:'inherit' },
  outlineBtn:    { padding:'10px 16px', borderRadius:11, border:'1px solid var(--bdr)', background:'var(--card)', color:'#7C6FF7', fontWeight:700, cursor:'pointer', fontSize:13, fontFamily:'inherit', textDecoration:'none', display:'inline-block', textAlign:'center' },
  editBtn:       { padding:'5px 12px', borderRadius:8, border:'1px solid var(--bdr)', background:'var(--bg)', color:'#7C6FF7', fontWeight:700, cursor:'pointer', fontSize:12, fontFamily:'inherit' },
  hint:          { fontSize:13, color:'var(--txl)', margin:0, lineHeight:1.6 },
  infoRow:       { display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid var(--bdr)', fontSize:14 },
  infoLbl:       { fontWeight:600, color:'var(--txm)' },
  infoVal:       { color:'var(--txt)' },
  colorRow:      { display:'flex', gap:7, flexWrap:'wrap', marginTop:2 },
  avatarPicker:  { display:'flex', gap:16, alignItems:'flex-start', flexWrap:'wrap' },
  avatarNote:    { fontSize:11, color:'var(--txl)', marginTop:4 },
  emojiGrid:     { display:'flex', flexWrap:'wrap', gap:4 },
  emojiBtn:      { width:34, height:34, borderRadius:8, border:'1px solid var(--bdr)', background:'var(--bg)', fontSize:18, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' },
  emojiBtnActive:{ border:'2px solid #7C6FF7', background:'var(--bdr)' },
  famNameRow:    { display:'flex', alignItems:'center', gap:10 },
  famName:       { fontSize:20, fontWeight:800, color:'#7C6FF7', flex:1 },
  memberGrid:    { display:'flex', gap:10, flexWrap:'wrap' },
  memberCard:    { background:'var(--bg)', borderRadius:12, padding:'12px 14px', textAlign:'center', minWidth:90 },
  memberAvatar:  { width:40, height:40, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:16, color:'#fff', margin:'0 auto 6px' },
  memberName:    { fontSize:13, fontWeight:700, color:'var(--txt)' },
  memberRole:    { fontSize:11, color:'var(--txl)', marginTop:2 },
  memberBirth:   { fontSize:11, color:'var(--txl)', marginTop:2 },
  memberSeen:    { fontSize:10, color:'var(--txl)', marginTop:2 },
  adminRow:        { background:'var(--bg)', borderRadius:12, padding:12, display:'flex', flexDirection:'column', gap:8 },
  adminTop:        { display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' },
  adminInfo:       { flex:1, minWidth:0 },
  adminName:       { fontSize:14, fontWeight:700, color:'var(--txt)' },
  adminEmail:      { fontSize:12, color:'var(--txl)' },
  adminRowInactive:{ opacity:.7 },
  inactiveBadge:   { background:'var(--red-bg)', color:'var(--red)', fontSize:10, fontWeight:700, padding:'2px 7px', borderRadius:20, marginLeft:6 },
  expandBtn:       { padding:'6px 12px', borderRadius:8, border:'1px solid var(--bdr)', background:'var(--bg)', color:'#7C6FF7', fontWeight:700, cursor:'pointer', fontSize:12, fontFamily:'inherit', flexShrink:0 },
  expandedPanel:   { display:'flex', flexDirection:'column', gap:14, paddingTop:12, borderTop:'1px solid var(--bdr)', marginTop:4 },
  subSection:      { display:'flex', flexDirection:'column', gap:8 },
  subTitle:        { fontSize:13, fontWeight:700, color:'var(--txm)' },
  actionGrid:      { display:'flex', flexWrap:'wrap', gap:8 },
  actionBtn:       { padding:'8px 14px', borderRadius:9, border:'1px solid var(--bdr)', background:'var(--card)', fontWeight:700, cursor:'pointer', fontSize:12, fontFamily:'inherit', color:'var(--txt)' },
  warnBtn:         { background:'#FFF7ED', borderColor:'#FED7AA', color:'#C2410C' },
  okBtn:           { background:'#F0FDF4', borderColor:'#86EFAC', color:'#166534' },
  dangerBtn:       { background:'#FFF5F5', borderColor:'#FCA5A5', color:'var(--red)' },
  roleSelect:      { padding:'6px 10px', borderRadius:8, border:'1px solid var(--input-bdr)', fontFamily:'inherit', fontSize:12, color:'var(--input-txt)', outline:'none', background:'var(--input-bg)', cursor:'pointer' },
  statusBadge:   { display:'inline-block', background:'var(--green-bg)', color:'var(--green)', padding:'6px 14px', borderRadius:20, fontSize:13, fontWeight:700 },
  inviteTypeBtn: { display:'flex', alignItems:'center', gap:12, padding:'14px 16px', borderRadius:12, border:'1px solid var(--bdr)', background:'var(--bg)', cursor:'pointer', fontFamily:'inherit', textAlign:'left', width:'100%' },
  modeHeader:    { display:'flex', alignItems:'center', gap:10, marginBottom:10 },
  backLink:      { background:'none', border:'none', color:'#7C6FF7', fontWeight:700, cursor:'pointer', fontSize:12, fontFamily:'inherit', padding:0 },
  btnRow:        { display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' },
  scopeInfo:     { fontSize:12, color:'var(--txm)', background:'var(--bg)', padding:'8px 12px', borderRadius:9 },
  locSearch:     { display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' },
  locResults:    { display:'flex', flexDirection:'column', gap:2, background:'var(--card)', border:'1px solid var(--bdr)', borderRadius:12, overflow:'hidden' },
  locResult:     { padding:'10px 14px', background:'none', border:'none', borderBottom:'1px solid var(--bdr)', cursor:'pointer', textAlign:'left', fontFamily:'inherit', fontSize:13, color:'var(--txt)' },
  locResultName: { display:'block' },
  locChosen:     { display:'flex', alignItems:'center', gap:12, background:'#F0FDF4', border:'1px solid #86EFAC', borderRadius:12, padding:'10px 14px' },
  locChosenIcon: { fontSize:20, flexShrink:0 },
  locChosenName: { fontSize:14, fontWeight:700, color:'var(--txt)' },
  locChosenCoords:{ fontSize:11, color:'var(--txm)', marginTop:2 },
  restoreBar:    { display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', marginTop:18, padding:'12px 16px', background:'var(--card)', border:'1px solid var(--bdr)', borderRadius:14 },
  restoreLabel:  { fontSize:12, fontWeight:700, color:'var(--txm)', flexShrink:0 },
  restoreChip:   { padding:'5px 12px', borderRadius:20, border:'1px dashed var(--bdr)', background:'var(--bg)', color:'var(--txm)', fontSize:12, fontWeight:600, cursor:'pointer', fontFamily:'inherit' },
  resetBtn:      { marginLeft:'auto', padding:'5px 12px', borderRadius:8, border:'1px solid var(--bdr)', background:'none', color:'var(--txl)', fontSize:12, cursor:'pointer', fontFamily:'inherit' },
};
